import {
  CloudUploadIcon,
  FilmIcon,
  MusicNoteIcon,
  PauseIcon,
  PlayIcon,
  SparklesIcon,
  UserGroupIcon,
} from '@heroicons/react/outline';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import { useEffect, useRef, useState } from 'react';

import clsxm from '@/lib/clsxm';

import LandingPageNavigation from '@/components/layout/LandingPageNavigation';
import ButtonLink from '@/components/links/ButtonLink';
import Seo from '@/components/Seo';

const Footer = dynamic(() => import('@/components/layout/Footer'));

const DEMO_BEFORE_URL =
  'https://media.easy-peasy.ai/landing/voice-changer-before.mp3';
const DEMO_AFTER_URL =
  'https://media.easy-peasy.ai/landing/voice-changer-after.mp3';

// Web-optimized (720p, ~1.8 MB) versions of a real conversion; preload='none'
// + posters keep the initial page weight to two small JPEGs.
const VIDEO_DEMOS = [
  {
    key: 'before' as const,
    src: 'https://media.easy-peasy.ai/landing/voice-changer-video-before.mp4',
    poster:
      'https://media.easy-peasy.ai/landing/voice-changer-video-before-poster.jpg',
  },
  {
    key: 'after' as const,
    src: 'https://media.easy-peasy.ai/landing/voice-changer-video-after.mp4',
    poster:
      'https://media.easy-peasy.ai/landing/voice-changer-video-after-poster.jpg',
  },
];

const SIGNUP_HREF = '/signup?preset=voice-changer';

// Public marketing page rendered at /voice-changer for logged-out visitors
// (and crawlers). Logged-in users get the tool itself instead.
const VoiceChangerLanding = () => {
  const { t } = useLocale('voice-changer');

  const [playing, setPlaying] = useState<'before' | 'after' | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const togglePlay = (which: 'before' | 'after') => {
    if (playing === which) {
      audioRef.current?.pause();
      setPlaying(null);
      return;
    }
    audioRef.current?.pause();
    const audio = new Audio(
      which === 'before' ? DEMO_BEFORE_URL : DEMO_AFTER_URL,
    );
    audio.onended = () => setPlaying(null);
    audio.onerror = () => setPlaying(null);
    audioRef.current = audio;
    audio.play().catch(() => setPlaying(null));
    setPlaying(which);
  };

  // Only one comparison video (and no demo audio) plays at a time.
  const handleVideoPlay = (key: string) => {
    audioRef.current?.pause();
    setPlaying(null);
    Object.entries(videoRefs.current).forEach(([k, el]) => {
      if (k !== key) el?.pause();
    });
  };

  const steps = [
    {
      name: t('Upload audio or video'),
      description: t(
        'Drop in an MP3, WAV, M4A, MP4 or MOV file — recordings up to 30 minutes.',
      ),
      icon: CloudUploadIcon,
    },
    {
      name: t('Pick a target voice'),
      description: t(
        'Choose from 1,000+ lifelike voices, or use a voice you have cloned or designed yourself.',
      ),
      icon: UserGroupIcon,
    },
    {
      name: t('Download the result'),
      description: t(
        'Get your converted audio in moments. For videos, the new voice is merged back into your video automatically.',
      ),
      icon: SparklesIcon,
    },
  ];

  const features = [
    {
      name: t('Your performance, untouched'),
      description: t(
        'Emotion, timing, laughs and pauses are preserved exactly as you performed them — only the voice changes.',
      ),
      icon: MusicNoteIcon,
    },
    {
      name: t('Works with video too'),
      description: t(
        'Upload a video and get the same video back with the new voice already in it — no editing software needed.',
      ),
      icon: FilmIcon,
    },
    {
      name: t('1,000+ voices and voice cloning'),
      description: t(
        'Pick any voice from our library, or clone your own voice and make any recording sound like you.',
      ),
      icon: UserGroupIcon,
    },
    {
      name: t('Studio-clean output'),
      description: t(
        'Optional background noise removal strips hiss and ambient sound before converting.',
      ),
      icon: SparklesIcon,
    },
  ];

  const faqs = [
    {
      question: t('How does the AI voice changer work?'),
      answer: t(
        'Upload a recording and pick a target voice. The AI analyzes your speech — the words, rhythm, and emotion — and re-renders it in the new voice, so the result sounds like a real performance rather than a robotic filter.',
      ),
    },
    {
      question: t('Can I change the voice in a video?'),
      answer: t(
        'Yes. Upload an MP4 or MOV video and the voice on its audio track is converted, then merged back into your video automatically. You download a finished video with the new voice.',
      ),
    },
    {
      question: t('Can I make a recording sound like my own voice?'),
      answer: t(
        'Yes. Clone or design a custom voice on Easy-Peasy.ai and it appears in the Voice Changer as a target — useful for re-voicing old recordings or dubbing content in your own voice.',
      ),
    },
    {
      question: t('How long can my recording be?'),
      answer: t(
        'Files up to 30 minutes and 100 MB are supported. Credits are based on the length of the recording, and every plan — including Free — comes with speech credits to try it out.',
      ),
    },
  ];

  const demoCards: {
    key: 'before' | 'after';
    label: string;
    title: string;
  }[] = [
    {
      key: 'before',
      label: t('Before'),
      title: t('Original recording'),
    },
    {
      key: 'after',
      label: t('After'),
      title: t('Same performance, new voice'),
    },
  ];

  return (
    <div className='relative isolate bg-white dark:bg-slate-900'>
      <Seo
        templateTitle={t('AI Voice Changer')}
        description={t(
          'Change your voice into any of 1,000+ realistic AI voices. Upload audio or video — every word, pause, and emotion stays exactly as you performed it.',
        )}
        image={`https://easy-peasy.ai/api/og-template?title=${t(
          'AI Voice Changer',
        )}`}
      />
      <LandingPageNavigation />

      {/* Hero */}
      <div className='relative pt-14'>
        <div
          className='absolute inset-x-0 -top-40 -z-10 transform-gpu overflow-hidden blur-3xl sm:-top-80'
          aria-hidden='true'
        >
          <div className='relative left-[calc(50%-11rem)] aspect-[1155/678] w-[36.125rem] -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#ff80b5] to-[#9089fc] opacity-30 sm:left-[calc(50%-30rem)] sm:w-[72.1875rem]'></div>
        </div>

        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl pt-32 sm:pt-48 lg:pt-24'>
            <div className='text-center'>
              <h1 className='text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-6xl'>
                {t('AI Voice Changer')}
              </h1>
              <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300'>
                {t(
                  'Change your voice into any of 1,000+ realistic AI voices. Upload audio or video — every word, pause, and emotion stays exactly as you performed it.',
                )}
              </p>
              <div className='mt-10 flex items-center justify-center gap-x-6'>
                <ButtonLink href={SIGNUP_HREF} variant='dark'>
                  {t('Get Started for Free')}
                </ButtonLink>
                <Link
                  href='/login?preset=voice-changer'
                  className='text-sm font-semibold leading-6 text-gray-900 dark:text-white'
                >
                  {t('Log in')} <span aria-hidden='true'>→</span>
                </Link>
              </div>
            </div>
          </div>

          {/* Before / after demo */}
          <div className='relative mx-auto mb-12 mt-16 max-w-3xl lg:mt-20'>
            <div className='rounded-3xl bg-gradient-to-b from-gray-50 to-gray-100 p-6 shadow-xl dark:from-slate-800 dark:to-slate-900 sm:p-8'>
              <p className='text-center text-sm font-medium text-gray-500 dark:text-gray-400'>
                {t('Hear it in action — one recording, two voices')}
              </p>
              <div className='mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2'>
                {demoCards.map((card) => (
                  <button
                    key={card.key}
                    type='button'
                    onClick={() => togglePlay(card.key)}
                    className={clsxm(
                      'group flex items-center gap-4 rounded-2xl border bg-white p-4 text-left shadow-sm transition-all hover:shadow-md dark:bg-slate-800',
                      playing === card.key
                        ? 'border-primary-500 ring-2 ring-primary-500/30'
                        : 'border-gray-200 dark:border-slate-700',
                    )}
                  >
                    <span
                      className={clsxm(
                        'flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full transition-colors',
                        playing === card.key
                          ? 'bg-primary-600 text-white'
                          : 'bg-primary-50 text-primary-600 group-hover:bg-primary-100 dark:bg-slate-700 dark:text-primary-400',
                      )}
                    >
                      {playing === card.key ? (
                        <PauseIcon className='h-6 w-6' />
                      ) : (
                        <PlayIcon className='h-6 w-6' />
                      )}
                    </span>
                    <span className='min-w-0'>
                      <span
                        className={clsxm(
                          'block text-xs font-semibold uppercase tracking-wide',
                          card.key === 'after'
                            ? 'text-primary-600 dark:text-primary-400'
                            : 'text-gray-400 dark:text-slate-500',
                        )}
                      >
                        {card.label}
                      </span>
                      <span className='block truncate text-sm font-medium text-gray-900 dark:text-white'>
                        {card.title}
                      </span>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* How it works */}
      <div className='bg-gray-50 py-24 dark:bg-slate-800 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('Change your voice in three steps')}
            </h2>
          </div>
          <div className='mx-auto mt-16 grid max-w-2xl grid-cols-1 gap-8 sm:mt-20 lg:mx-0 lg:max-w-none lg:grid-cols-3'>
            {steps.map((step, index) => (
              <div
                key={step.name}
                className='flex flex-col rounded-2xl border border-gray-200 bg-white p-8 dark:border-slate-700 dark:bg-slate-900'
              >
                <div className='flex items-center gap-4'>
                  <span className='flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600 text-white'>
                    <step.icon className='h-6 w-6' />
                  </span>
                  <span className='text-sm font-semibold text-primary-600 dark:text-primary-400'>
                    {t('Step')} {index + 1}
                  </span>
                </div>
                <h3 className='mt-4 text-lg font-semibold text-gray-900 dark:text-white'>
                  {step.name}
                </h3>
                <p className='mt-2 text-base leading-7 text-gray-600 dark:text-gray-300'>
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Video before/after comparison */}
      <div className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('Change the voice in a video')}
            </h2>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300'>
              {t(
                'The same clip before and after — the converted voice is merged back into the video automatically.',
              )}
            </p>
          </div>
          <div className='mx-auto mt-12 grid max-w-5xl grid-cols-1 gap-6 sm:mt-16 md:grid-cols-2'>
            {VIDEO_DEMOS.map((demo) => (
              <figure key={demo.key}>
                <figcaption className='mb-3 flex items-center gap-2'>
                  <span
                    className={clsxm(
                      'rounded-full px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide',
                      demo.key === 'after'
                        ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                        : 'bg-gray-100 text-gray-500 dark:bg-slate-700 dark:text-slate-300',
                    )}
                  >
                    {demo.key === 'after' ? t('After') : t('Before')}
                  </span>
                  <span className='text-sm font-medium text-gray-900 dark:text-white'>
                    {demo.key === 'after'
                      ? t('Same video, new voice')
                      : t('Original video')}
                  </span>
                </figcaption>
                <video
                  ref={(el) => {
                    videoRefs.current[demo.key] = el;
                  }}
                  controls
                  preload='none'
                  playsInline
                  poster={demo.poster}
                  src={demo.src}
                  onPlay={() => handleVideoPlay(demo.key)}
                  className='aspect-video w-full rounded-2xl bg-black shadow-lg'
                />
              </figure>
            ))}
          </div>
        </div>
      </div>

      {/* Features */}
      <div className='bg-gray-50 py-24 dark:bg-slate-800 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-2xl lg:text-center'>
            <h2 className='text-base font-semibold leading-7 text-primary-600 dark:text-primary-500'>
              {t('Voice Changer')}
            </h2>
            <p className='mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('A new voice, not a new performance')}
            </p>
            <p className='mt-6 text-lg leading-8 text-gray-600 dark:text-gray-300'>
              {t(
                'Perfect for voiceovers, dubbing, character voices, podcasts, and re-voicing videos without re-recording a single take.',
              )}
            </p>
          </div>
          <div className='mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-4xl'>
            <dl className='grid max-w-xl grid-cols-1 gap-x-8 gap-y-10 lg:max-w-none lg:grid-cols-2 lg:gap-y-16'>
              {features.map((feature) => (
                <div key={feature.name} className='relative pl-16'>
                  <dt className='text-base font-semibold leading-7 text-gray-900 dark:text-white'>
                    <div className='absolute left-0 top-0 flex h-10 w-10 items-center justify-center rounded-lg bg-primary-600 text-white dark:bg-primary-500'>
                      <feature.icon className='h-6 w-6' />
                    </div>
                    {feature.name}
                  </dt>
                  <dd className='mt-2 text-base leading-7 text-gray-600 dark:text-gray-300'>
                    {feature.description}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* FAQ */}
      <div className='py-24 sm:py-32'>
        <div className='mx-auto max-w-7xl px-6 lg:px-8'>
          <div className='mx-auto max-w-4xl'>
            <h2 className='text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl'>
              {t('Frequently asked questions')}
            </h2>
            <dl className='mt-10 space-y-8'>
              {faqs.map((faq) => (
                <div key={faq.question}>
                  <dt className='text-base font-semibold leading-7 text-gray-900 dark:text-white'>
                    {faq.question}
                  </dt>
                  <dd className='mt-2 text-base leading-7 text-gray-600 dark:text-gray-300'>
                    {faq.answer}
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        </div>
      </div>

      {/* CTA */}
      <div className='bg-primary-600 dark:bg-primary-900'>
        <div className='px-6 py-24 sm:px-6 sm:py-32 lg:px-8'>
          <div className='mx-auto max-w-2xl text-center'>
            <h2 className='text-3xl font-bold tracking-tight text-white sm:text-4xl'>
              {t('Try the AI Voice Changer today')}
            </h2>
            <p className='mx-auto mt-6 max-w-xl text-lg leading-8 text-primary-100'>
              {t(
                'Upload a recording, pick a voice, and hear your words performed by someone new — in seconds.',
              )}
            </p>
            <div className='mt-10 flex items-center justify-center gap-x-6'>
              <ButtonLink
                href={SIGNUP_HREF}
                variant='light'
                className='rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-primary-600 shadow-sm hover:bg-primary-50'
              >
                {t('Get Started for Free')}
              </ButtonLink>
              <Link
                href='/login?preset=voice-changer'
                className='text-sm font-semibold leading-6 text-white'
              >
                {t('Log in')} <span aria-hidden='true'>→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default VoiceChangerLanding;
